import * as yup from "yup";
import { createAddressFields } from "../constants";
import { type FormSectionConfig, type TaxFormConfig, type SelectOption } from "../types";

export const TAX_CLASSIFICATION_OPTIONS: SelectOption[] = [
  { value: "individual_or_sole_proprietor", label: "Individual/sole proprietor" },
  { value: "c_corporation", label: "C Corporation" },
  { value: "s_corporation", label: "S corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "trust_or_estate", label: "Trust/estate" },
  { value: "llc", label: "LLC" },
  { value: "other", label: "Other" },
];

export const LLC_TAX_CLASSIFICATION_OPTIONS: SelectOption[] = [
  { value: "c_corporation", label: "C corporation" },
  { value: "s_corporation", label: "S corporation" },
  { value: "partnership", label: "Partnership" },
];

export const EXEMPT_PAYEE_CODE_OPTIONS: SelectOption[] = [
  {
    value: "1",
    label:
      "1—An organization exempt from tax under section 501(a), any IRA, or a custodial account under section 403(b)(7) if the account satisfies the requirements of section 401(f)(2)",
  },
  { value: "2", label: "2—The United States or any of its agencies or instrumentalities" },
  {
    value: "3",
    label:
      "3—A state, the District of Columbia, a U.S. commonwealth or territory, or any of their political subdivisions or instrumentalities",
  },
  {
    value: "4",
    label:
      "4—A foreign government or any of its political subdivisions, agencies, or instrumentalities",
  },
  { value: "5", label: "5—A corporation" },
  {
    value: "6",
    label:
      "6—A dealer in securities or commodities required to register in the United States, the District of Columbia, or a U.S. commonwealth or territory",
  },
  {
    value: "7",
    label:
      "7—A futures commission merchant registered with the Commodity Futures Trading Commission",
  },
  { value: "8", label: "8—A real estate investment trust" },
  {
    value: "9",
    label:
      "9—An entity registered at all times during the tax year under the Investment Company Act of 1940",
  },
  { value: "10", label: "10—A common trust fund operated by a bank under section 584(a)" },
  { value: "11", label: "11—A financial institution as defined under section 581" },
  {
    value: "12",
    label: "12—A middleman known in the investment community as a nominee or custodian",
  },
  {
    value: "13",
    label: "13—A trust exempt from tax under section 664 or described in section 4947",
  },
];

export const FATCA_EXEMPTION_CODE_OPTIONS: SelectOption[] = [
  {
    value: "A",
    label:
      "A—An organization exempt from tax under section 501(a) or any individual retirement plan as defined in section 7701(a)(37)",
  },
  { value: "B", label: "B—The United States or any of its agencies or instrumentalities" },
  {
    value: "C",
    label:
      "C—A state, the District of Columbia, a U.S. commonwealth or territory, or any of their political subdivisions or instrumentalities",
  },
  {
    value: "D",
    label:
      "D—A corporation the stock of which is regularly traded on one or more established securities markets, as described in Regulations section 1.1472-1(c)(1)(i)",
  },
  {
    value: "E",
    label:
      "E—A corporation that is a member of the same expanded affiliated group as a corporation described in Regulations section 1.1472-1(c)(1)(i)",
  },
  {
    value: "F",
    label:
      "F—A dealer in securities, commodities, or derivative financial instruments (including notional principal contracts, futures, forwards, and options) that is registered as such under the laws of the United States or any state",
  },
  { value: "G", label: "G—A real estate investment trust" },
  {
    value: "H",
    label:
      "H—A regulated investment company as defined in section 851 or an entity registered at all times during the tax year under the Investment Company Act of 1940",
  },
  { value: "I", label: "I—A common trust fund as defined in section 584(a)" },
  { value: "J", label: "J—A bank as defined in section 581" },
  { value: "K", label: "K—A broker" },
  {
    value: "L",
    label: "L—A trust exempt from tax under section 664 or described in section 4947(a)(1)",
  },
  {
    value: "M",
    label: "M—A tax-exempt trust under a section 403(b) plan or section 457(g) plan",
  },
];

const sections: FormSectionConfig[] = [
  {
    id: "identification",
    title: "",
    fields: [
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "Enter name",
        required: true,
        gridColumn: "full",
      },
      {
        name: "disregardedEntityName",
        type: "text",
        label: "Disregarded entity name",
        placeholder: "Enter disregarded entity name",
        gridColumn: "full",
      },
      {
        name: "taxClassification",
        type: "select",
        label: "Tax classification",
        placeholder: "Select a tax classification",
        required: true,
        gridColumn: "full",
        options: TAX_CLASSIFICATION_OPTIONS,
      },
      {
        name: "llcTaxClassification",
        type: "select",
        label: "LLC tax classification",
        placeholder: "Select a tax classification",
        tooltip: {
          content:
            "For LLC, select the appropriate code for the tax classification of the LLC, unless it is a disregarded entity. A disregarded entity should instead select the tax classification of its owner.",
          placement: "right",
        },
        gridColumn: "full",
        options: LLC_TAX_CLASSIFICATION_OPTIONS,
        showWhen: (values) => values.taxClassification === "llc",
      },
      {
        name: "exemptPayeeCode",
        type: "select",
        label: "Exempt payee code",
        placeholder: "Select an exempt payee code",
        gridColumn: "half",
        options: EXEMPT_PAYEE_CODE_OPTIONS,
      },
      {
        name: "fatcaExemptionCode",
        type: "select",
        label: "FATCA exemption code",
        placeholder: "Select a FATCA exemption code",
        gridColumn: "half",
        options: FATCA_EXEMPTION_CODE_OPTIONS,
      },
      {
        name: "_addressLabel",
        type: "label",
        label: "Address",
        required: true,
        linkedField: "address",
        gridColumn: "full",
      },
      ...createAddressFields("address"),
    ],
  },
  {
    id: "tin",
    title: "Taxpayer Identification Number (TIN)",
    fields: [
      {
        name: "taxId",
        type: "text",
        label: "Tax ID",
        placeholder: "SSN or EIN",
        tooltip: {
          content: "Tax ID is a 9-character identifier.",
        },
        required: true,
        gridColumn: "half",
      },
    ],
  },
];

const validationSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  taxClassification: yup.string().required("Tax classification is required"),
  address: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  taxId: yup
    .string()
    .required("Tax ID is required")
    .length(9, "Tax ID must be exactly 9 characters"),
});

const initialValues = {
  name: "",
  disregardedEntityName: "",
  taxClassification: "",
  llcTaxClassification: "",
  exemptPayeeCode: "",
  fatcaExemptionCode: "",
  address: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  taxId: "",
};

export const w9Config: TaxFormConfig = {
  formId: "W9",
  title: "New FATCA details",
  subtitle: "W-9",
  sections,
  validationSchema,
  initialValues,
};
